import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, shareReplay } from 'rxjs';

export const BP = {
  sm: 36, // rem
  md: 44,
  lg: 60,
  xl: 72,
  '2xl': 84,
} as const;
export type BreakpointKey = keyof typeof BP;

const unit = 'rem';
const up = (v: number) => `(min-width: ${v}${unit})`;
const down = (v: number) => `(max-width: calc(${v}${unit} - 1px))`;
const between = (min: number, maxExclusive: number) =>
  `(min-width: ${min}${unit}) and (max-width: calc(${maxExclusive}${unit} - 1px))`;

@Injectable({ providedIn: 'root' })
export class Responsive {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly queries = {
    sm: up(BP.sm),
    md: up(BP.md),
    lg: up(BP.lg),
    xl: up(BP.xl),
    '2xl': up(BP['2xl']),
  } as const;

  private state$ = this.breakpointObserver
    .observe(Object.values(this.queries))
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private signalForQuery(q: string) {
    return toSignal(this.state$.pipe(map((s) => s.breakpoints[q])), { initialValue: false });
  }

  readonly smUp = this.signalForQuery(this.queries.sm);
  readonly mdUp = this.signalForQuery(this.queries.md);
  readonly lgUp = this.signalForQuery(this.queries.lg);
  readonly xlUp = this.signalForQuery(this.queries.xl);
  readonly twoXlUp = this.signalForQuery(this.queries['2xl']);

  atLeast(key: BreakpointKey) {
    return this.signalForQuery(this.queries[key]);
  }

  below(key: BreakpointKey) {
    return this.match$(down(BP[key]));
  }

  betweenKeys(min: BreakpointKey, maxExclusive: BreakpointKey) {
    return this.match$(between(BP[min], BP[maxExclusive]));
  }

  between(minRem: number, maxRemExclusive: number) {
    return this.match$(between(minRem, maxRemExclusive));
  }

  match$(query: string) {
    return toSignal(this.breakpointObserver.observe([query]).pipe(map((r) => r.matches)), {
      initialValue: false,
    });
  }
}
